/**
 * Share service — the boundary between the funnel and the public share target.
 *
 * Contract: a share link points at `/r/:code` on THIS origin (never a hardcoded
 * domain — that was the 404) and resolves against `card_shares`, which holds
 * presentation data only.
 */
import { supabase } from "@/integrations/supabase/client";

export interface PublicCardShare {
  code: string;
  avatarUrl: string;
  colour: string;
  cardStyle: string | null;
  orientation: "portrait" | "landscape";
  /**
   * The SHARER'S issued referral code, set server-side when the link was
   * published. Null when the sharer had no code yet. This — not the share slug
   * — is what arrival capture stores as `ref`.
   */
  referralCode: string | null;
}

/**
 * THE canonical share URL — `/r/:code` on THIS origin.
 *
 * HISTORY, so this does not get "fixed" back: it briefly pointed at the
 * `share-page` edge function, which serves real HTML with per-card og:* tags in
 * the bytes. That worked when built and is now DEAD THROUGH NO FAULT OF OURS —
 * the functions gateway rewrites any markup response to
 * `content-type: text/plain` plus `content-security-policy: default-src 'none';
 * sandbox`. Verified 2026-08-01: `text/html` AND `application/xhtml+xml` both
 * come back as `text/plain`. Consequences of pointing shares there:
 *   - recipients in a real browser saw the RAW HTML SOURCE (the reported bug) —
 *     text/plain means the meta-refresh and the location.replace never run;
 *   - scrapers saw no HTML document at all, so no unfurl.
 *
 * So shares point at the app route again. Humans get the working landing page
 * (card + CTA + gallery link + ref capture). Unfurls fall back to the SITE-level
 * og:image from index.html — branded, but NOT the individual card. Per-card
 * unfurl needs the HTML served from this origin, i.e. SSR.
 *
 * `share-page` / `share-og` are kept: `share-og` still returns a correct
 * 1200x630 image/png per code and becomes the og:image the moment there is a
 * server that can emit the tag.
 */
export function buildShareUrl(code: string): string {
  return `${window.location.origin}/r/${code}`;
}


/**
 * A share LINK ID — not a referral code.
 *
 * `card_shares` is keyed by an opaque slug, so a share link needs one. It is
 * deliberately lowercase and unshaped so it can never be mistaken for, or
 * presented as, a redeemable referral code (those come from the B7
 * `my_referral_code` issuer and do not exist yet). Never render it to the user.
 */
const SHARE_SLUG_KEY = "cmoji.share.slug";

export function getOrCreateShareSlug(): string {
  try {
    const existing = localStorage.getItem(SHARE_SLUG_KEY);
    if (existing) return existing;
  } catch {
    /* storage unavailable */
  }
  const AB = "abcdefghijkmnpqrstuvwxyz23456789";
  let slug = "";
  for (let i = 0; i < 12; i++) slug += AB[Math.floor(Math.random() * AB.length)];
  try {
    localStorage.setItem(SHARE_SLUG_KEY, slug);
  } catch {
    /* storage unavailable */
  }
  return slug;
}

/** The human-readable destination (what the link actually opens). */
export function shareUrlLabel(code: string): string {
  return `${window.location.host}/r/${code}`;
}


/**
 * Publish the share target. Must be awaited BEFORE the OS share sheet opens,
 * otherwise the recipient can open the link before the row exists.
 */
export async function publishCardShare(input: {
  code: string;
  avatarUrl: string;
  colour: string;
  cardStyle?: string | null;
  orientation: "portrait" | "landscape";
}): Promise<boolean> {
  const { data, error } = await supabase.functions.invoke("create-card-share", {
    body: {
      code: input.code,
      avatarUrl: input.avatarUrl,
      colour: input.colour,
      cardStyle: input.cardStyle ?? null,
      orientation: input.orientation,
    },
  });
  if (error) return false;
  return Boolean((data as { ok?: boolean } | null)?.ok);
}

/**
 * Public read — works for a signed-out recipient.
 *
 * Goes through the code-scoped `get_public_card_share` resolver, NOT a table
 * read: `card_shares` has no SELECT grant for anon or authenticated, because a
 * share link is consent to reveal ONE card, not to list every share.
 */
export async function getPublicCardShare(code: string): Promise<PublicCardShare | null> {
  const { data, error } = await supabase
    .rpc("get_public_card_share", { p_code: code })
    .maybeSingle();

  if (error || !data) return null;
  const row = data as {
    code: string;
    avatar_url: string;
    card_colour: string;
    card_style: string | null;
    orientation: string | null;
    referral_code: string | null;
  };
  return {
    code: row.code,
    avatarUrl: row.avatar_url,
    colour: row.card_colour,
    cardStyle: row.card_style,
    orientation: row.orientation === "landscape" ? "landscape" : "portrait",
    referralCode: row.referral_code ?? null,
  };
}
